
import React, { useState } from 'react';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, InfoIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface DatePickerProps {
  date: Date | undefined;
  onSelect: (date: Date | undefined) => void;
  onDurationSelect?: (days: number) => void;
}

const DatePicker: React.FC<DatePickerProps> = ({ date, onSelect, onDurationSelect }) => {
  // Track currently selected forecast duration
  const [selectedDuration, setSelectedDuration] = useState<number>(1);
  // State to control the open state of the popover
  const [open, setOpen] = useState(false);

  // Custom onSelect handler to close the popover after selection
  const handleDateSelect = (newDate: Date | undefined) => {
    onSelect(newDate);
    setOpen(false); // Close the popover after selection
  };

  // Handler for selecting duration
  const handleDurationSelect = (days: number) => {
    setSelectedDuration(days);
    if (onDurationSelect) {
      // Ensure the parent component gets the update
      setTimeout(() => {
        onDurationSelect(days);
      }, 0);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Date</h3>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <InfoIcon className="h-4 w-4 text-blue-500 cursor-help" />
            </TooltipTrigger>
            <TooltipContent>
              <p className="w-[200px] text-sm">Select any date for your forecast</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full border-2 justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? format(date, "EEEE d MMMM yyyy", { locale: enUS }) : <span>Select a date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateSelect}
            initialFocus
            className="p-3 pointer-events-auto"
            locale={enUS}
          />
        </PopoverContent>
      </Popover>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">Forecast duration</h3>
        <div className="grid grid-cols-2 gap-2">
          <Button 
            variant={selectedDuration === 1 ? "default" : "outline"}
            size="sm"
            onClick={() => handleDurationSelect(1)}
            className="text-xs sm:text-sm"
          >
            1 day
          </Button>
          <Button 
            variant={selectedDuration === 3 ? "default" : "outline"}
            size="sm"
            onClick={() => handleDurationSelect(3)}
            className="text-xs sm:text-sm"
          >
            3 days
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DatePicker;
